/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "react-oidc-context";

import { Layout } from "./components/Layout.tsx";
import { Loading } from "./components/ui.tsx";
import { LoginPage } from "./features/auth/LoginPage.tsx";
import { HomeView } from "./features/home/HomeView.tsx";
import { FamilyView } from "./features/family/FamilyView.tsx";
import { PlanView } from "./features/plan/PlanView.tsx";
import { RecipesView } from "./features/recipes/RecipesView.tsx";
import { RecipeDetailView } from "./features/recipes/RecipeDetailView.tsx";
import { RecipeEditor } from "./features/recipes/RecipeEditor.tsx";
import {
  GenerateRecipeView, ImportRecipeView, ScanRecipeView,
} from "./features/recipes/AiViews.tsx";
import { FirstRunWizard } from "./features/onboarding/FirstRunWizard.tsx";
import { ShoppingListsView } from "./features/shopping/ShoppingListsView.tsx";
import { ShoppingListView } from "./features/shopping/ShoppingListView.tsx";
import { ShoppingModeView } from "./features/shopping/ShoppingModeView.tsx";
import { SettingsView } from "./features/settings/SettingsView.tsx";
import { CirclesListView } from "./features/circles/CirclesListView.tsx";
import { CircleDetailView } from "./features/circles/CircleDetailView.tsx";
import { TodayProvider } from "./lib/useToday.tsx";
import { ToastProvider } from "./components/Toast.tsx";

export default function App() {
  const auth = useAuth();

  if (auth.activeNavigator || auth.isLoading) {
    return <div className="grid min-h-screen place-items-center"><Loading label="Signing you in…" /></div>;
  }
  if (!auth.isAuthenticated) return <LoginPage />;

  return (
    // Mounted INSIDE the auth gate: /api/today needs a token, and the household
    // timezone is what makes the answer meaningful.
    <TodayProvider>
      <ToastProvider>
      <Routes>
        {/* Shopping mode is full-bleed — no app chrome competing with the list. */}
        <Route path="/shopping/:id/mode" element={<ShoppingModeView />} />
        <Route element={<Layout />}>
          <Route path="/" element={<HomeView />} />
          {/* Inside the shell, not full-bleed: the wizard is skippable at every
              stage, and hiding the nav would make "skip" feel like an escape
              from somewhere you shouldn't have been. */}
          <Route path="/welcome" element={<FirstRunWizard />} />
          <Route path="/plan" element={<PlanView />} />
          <Route path="/recipes" element={<RecipesView />} />
          {/* Static segments BEFORE :id, or "generate" is read as a recipe id
              and rejected by the UUID guard. */}
          <Route path="/recipes/new" element={<RecipeEditor />} />
          <Route path="/recipes/generate" element={<GenerateRecipeView />} />
          <Route path="/recipes/scan" element={<ScanRecipeView />} />
          <Route path="/recipes/import" element={<ImportRecipeView />} />
          <Route path="/recipes/:id" element={<RecipeDetailView />} />
          <Route path="/recipes/:id/edit" element={<RecipeEditor />} />
          <Route path="/shopping" element={<ShoppingListsView />} />
          <Route path="/shopping/:id" element={<ShoppingListView />} />
          <Route path="/family" element={<FamilyView />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="/circles" element={<CirclesListView />} />
          <Route path="/circles/:id" element={<CircleDetailView />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      </ToastProvider>
    </TodayProvider>
  );
}
